import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { LOADING_SPINNER_COLOR } from '@/lib/loadingSpinnerColor';
import { ActivityIndicator, Modal, Platform, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WebView } from 'react-native-webview';
import { XStack } from 'tamagui';
import { CardTitle } from '@/components/ui/Typography';

interface PdfViewerModalProps {
  /** Signed file URL to display; null hides the modal. */
  url: string | null;
  title?: string;
  onClose: () => void;
}

/**
 * In-app PDF viewer (native). WKWebView renders PDFs natively on iOS and is
 * chromeless — the credential-carrying signed URL is never visible to the
 * user, and they stay in the app instead of bouncing out to Safari.
 */
export const PdfViewerModal = ({ url, title, onClose }: PdfViewerModalProps) => {
  const insets = useSafeAreaInsets();

  if (Platform.OS === 'web') return null;

  return (
    <Modal
      visible={!!url}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={onClose}
    >
      <View style={{ flex: 1, backgroundColor: '#F8F7F4', paddingTop: insets.top }}>
        <XStack
          ai="center"
          jc="space-between"
          px="$3"
          py="$2"
          backgroundColor="#F8F7F4"
          borderBottomWidth={1}
          borderBottomColor="#ECEAE4"
        >
          <CardTitle fontWeight="600" color="#0D0D0D" numberOfLines={1} flex={1} pr="$3">
            {title?.trim() || 'PDF'}
          </CardTitle>
          <TouchableOpacity
            onPress={onClose}
            style={{
              width: 36,
              height: 36,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 18,
            }}
            accessibilityLabel="Close PDF viewer"
          >
            <Ionicons name="close" size={26} color="#0D0D0D" />
          </TouchableOpacity>
        </XStack>
        {url && (
          <WebView
            source={{ uri: url }}
            style={{ flex: 1, backgroundColor: '#F8F7F4' }}
            originWhitelist={['https://*']}
            startInLoadingState
            renderLoading={() => (
              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ActivityIndicator size="large" color={LOADING_SPINNER_COLOR} />
              </View>
            )}
          />
        )}
      </View>
    </Modal>
  );
};
